import { action, observable, runInAction } from 'mobx';
import { DiagnosisCodeEditForm } from 'application/diagnosis-code/forms';
import { BaseEditViewStore, BaasicDropdownStore } from 'core/stores';
import { applicationContext } from 'core/utils';
import { DiagnosisCodeService } from 'application/import/services';
import { ItemCategoryService } from 'application/product/services';
import { LookupService } from "common/services";
import _ from 'lodash';

@applicationContext
class DiagnosisCodeEditViewStore extends BaseEditViewStore {
    @observable accessor showJustificationType = false;

    constructor(rootStore) {
        const id = rootStore.routerStore.routerState.params.id;
        const diagnosisCodeService = new DiagnosisCodeService(rootStore.application.baasic.apiClient);
        const itemCategoryService = new ItemCategoryService(rootStore.application.baasic.apiClient);
        const lookupService = new LookupService(rootStore.application.baasic.apiClient);

        super(rootStore, {
            name: 'diagnosis-code-edit',
            id: id,
            Form: DiagnosisCodeEditForm,
            authorization: 'portalSection.update',
            actions: () => {
                return {
                    get: async (id, options) => {
                        const response = await diagnosisCodeService.get(id, {...options, embed:"DiagnosisCodeCategories,DiagnosisCodeJustificationTypes"});
                        response.data.diagnosisCodeJustificationTypes = response.data.diagnosisCodeJustificationTypes && response.data.diagnosisCodeJustificationTypes.map(i=> i.justificationType);
                        response.data.diagnosisCodeCategories = response.data.diagnosisCodeCategories && response.data.diagnosisCodeCategories.map(i=> i.itemCategory);
                        return response.data;
                    },
                    update: async (resource) => {
                        resource.diagnosisCodeJustificationTypes = resource.diagnosisCodeJustificationTypes && resource.diagnosisCodeJustificationTypes.map(i=> { return { justificationTypeId: i.id }});
                        resource.diagnosisCodeCategories = resource.diagnosisCodeCategories && resource.diagnosisCodeCategories.map(i=> { return { itemCategoryId: i.id }});
                        const response = await diagnosisCodeService.update(resource);
                        return response.data;
                    },
                    create: async (resource) => {
                        resource.diagnosisCodeJustificationTypes = resource.diagnosisCodeJustificationTypes && resource.diagnosisCodeJustificationTypes.map(i=> { return { justificationTypeId: i.id }});
                        resource.diagnosisCodeCategories = resource.diagnosisCodeCategories && resource.diagnosisCodeCategories.map(i=> { return { itemCategoryId: i.id }});
                        const response = await diagnosisCodeService.create(resource);
                        return response.data;
                    }
                }
            },
            FormClass: DiagnosisCodeEditForm,
        });

        this.itemCategoryStore = new BaasicDropdownStore({
			filterable: true,
			multi:true,
            initFetch: false
		}, {
			fetchFunc: async(term) => {
				const response = await itemCategoryService.find({ 
					sort:"name|asc",
					searchQuery: term, 
					rpp: 100
				});

                let filteredCategories = response.data.item.filter(i => i.abrv === "enteral" || i.abrv === "respiratory" || i.abrv === "urological" 
                        || i.abrv === "ostomy" || i.abrv === "wound-care");
                return filteredCategories;
			},
            onChange: async () => {
                this.showJustificationType = this.itemCategoryStore.value.some(d => d.name === "Enteral" || d.name === "Urological");
                if (this.showJustificationType) {
                    await this.justificationTypesStore.filterAsync();
                    if(this.form.$("diagnosisCodeJustificationTypes").value){
                        let newValue = this.form.$("diagnosisCodeJustificationTypes").value.filter(x => this.form.$("diagnosisCodeCategories").value.some(y => x.itemCategoryId === y.id));
                        this.justificationTypesStore.setValue(newValue);
                        this.form.set({ diagnosisCodeJustificationTypes: newValue });
                    }
                }
                else {
                    this.justificationTypesStore.setValue(null);
                    this.form.set({ diagnosisCodeJustificationTypes: null });
                }
            }
		});

        this.justificationTypesStore = new BaasicDropdownStore({
			multi:true,
            initFetch: false
		}, {
			fetchFunc: async() => {
                const response = await lookupService.getLookups('SpecialtyType');
                return _.orderBy(response.specialtyType.filter(c => this.form.$("diagnosisCodeCategories").value && this.form.$("diagnosisCodeCategories").value.some(d => d.id === c.itemCategoryId && (c.abrv === "diabetic-formula" || c.abrv === "elemental-formula" || c.abrv === "pulmonary-formula" || c.abrv === "protein-supplement" || c.abrv === "pump" || c.abrv === "silicone-catheter" || c.abrv === "coude-catheter" || c.abrv === "3-way-catheter" || c.abrv === "renal-formula"))), ['name'], ['asc']);
			}
		});
    }

    @action.bound
    async onInit({ initialLoad }) {
        if(!initialLoad) {
            this.rootStore.routerStore.goBack();
        }
        await this.itemCategoryStore.filterAsync();
        await this.justificationTypesStore.filterAsync();
        runInAction(() => {
            this.showJustificationType = this.form.$("diagnosisCodeCategories").value && this.form.$("diagnosisCodeCategories").value.some(d => d.name === "Enteral" || d.name === "Urological");
        });
    }

    @action.bound
    notifySuccessCreate() {
        this.rootStore.notificationStore.success('DIAGNOSIS_CODE.CREATE.SUCCESS');
    }

    @action.bound
    notifySuccessUpdate(name) {
        this.rootStore.notificationStore.success('DIAGNOSIS_CODE.EDIT.SUCCESS', name);
    }

    @action.bound
    async updateResource(resource) {
        if (!this.actions.update) return;

        this.form.setFieldsDisabled(true);
        try {
            if (this.translationStore) {
                this.translationStore.applyMetadata(resource);
            }

            await this.actions.update({
                id: this.id,
                ...resource
            });

            this.form.setFieldsDisabled(false);

            await this.rootStore.routerStore.goBack();
            await setTimeout(() => this.notifySuccessUpdate(this.name), 10);
        }
        catch(err) {
            this.form.setFieldsDisabled(false);

            if(err && err.data && err.data.message && err.data.message === "Resource already exists."){
                this.rootStore.notificationStore.error("This DX Code already exists.");
            }else{
                return this.onUpdateError(err);
            }
        }
    }

    @action.bound
    async createResource(resource) {
        if (!this.actions.create) return;

        this.form.setFieldsDisabled(true);
        try {
            if (this.translationStore) {
                this.translationStore.applyMetadata(resource);
            }

            await this.actions.create(resource);

            this.form.setFieldsDisabled(false);

            await this.rootStore.routerStore.goBack();
            await setTimeout(() => this.notifySuccessCreate(), 10);
        }
        catch(err) {
            this.form.setFieldsDisabled(false);

            if(err && err.data && err.data.message && err.data.message === "Resource already exists."){
                this.rootStore.notificationStore.error("This DX Code already exists.");
            }else{
                return this.onCreateError(err);
            }
        }
    }
}

export default DiagnosisCodeEditViewStore;
